import React from "react";
import { toKebabCase } from "@qlik/qlik/src/lib/utils";
import LinkWithDescription from "@qlik/qlik/src/components/Header/Sub_Menu_Components/Link_With_Description";
import { renderSanitizedHtmlContent } from "@qlik/qlik/src/lib/util/helpers";

/**
 * SubmenuLayoutFour renders the right hand side of a main menu drop down, with
 * left: two columns of LinkWithDescription[], right: one column of LinkWithDescription[]
 * e.g. product > all products
 */
const SubmenuLayoutFour = ({ data: subItems, layoutType, layoutId, className = "", deviceType = "desktop" }) => {
    const oneColWidthClass = "xl:basis-1/3";
    const twoColWidthClass = "xl:basis-2/3";

    const layoutClass = toKebabCase(layoutType);

    // Helper to render the inner content of a subItem (title + list of links)
    // isOuterTwoCol: true if this subItem is a "2 Col" type AND it's rendered in its own main block
    // isOuterOneCol: true if this subItem is a "1 Col" type AND it's rendered in its own main block
    const renderSubItemContent = (subItemData, { isOuterTwoCol = false } = {}) => {
        let ulClassName = "grid mt-5 grid-cols-1 gap-y-7.5";
        if (isOuterTwoCol) {
            // A standalone "2 Col" item
            ulClassName += " gap-x-15 gap-y-7.5 lg:grid-cols-2";
        } else {
            // This covers standalone "1 Col" items and items within a "1 Col" group
            ulClassName += " gap-3.75";
        }

        return (
            <div>
                <p
                    className="text-md font-bold leading-none tracking-normal mb-5 pb-3.5 border-b border-b-gray/25"
                    dangerouslySetInnerHTML={{ __html: renderSanitizedHtmlContent(subItemData?.title) }}
                />
                {subItemData.subItems && (
                    <ul className={ulClassName}>
                        {subItemData.subItems?.map((subItemChild) => (
                            <li key={subItemChild.id}>
                                <LinkWithDescription data={subItemChild} />
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        );
    };

    const determineSubItemWidthClass = (subItemData) => {
        // Ensure itemName exists and is a string before calling .includes()
        const itemNameStr = subItemData?.itemName || "";
        const isTwoCol = itemNameStr.includes("2 Col");
        const isOneCol = itemNameStr.includes("1 Col");

        return isTwoCol ? twoColWidthClass : isOneCol ? oneColWidthClass : twoColWidthClass; // Default to two col width
    };

    const renderedBlocks = [];

    if (subItems && subItems.length > 0) {
        if (subItems.length === 3) {
            const item0IsOneCol = subItems[0]?.itemName.includes("1 Col");
            const item1IsOneCol = subItems[1]?.itemName.includes("1 Col");
            const item2IsOneCol = subItems[2]?.itemName.includes("1 Col");

            if (item0IsOneCol && item1IsOneCol) {
                // Group and render first and second items (items 0 and 1)
                renderedBlocks.push(
                    <div className={`${oneColWidthClass} max-xl:mb-15`} key="group-0-1">
                        <div className="max-xl:gap-15 flex flex-col gap-10">
                            {renderSubItemContent(subItems[0])}
                            {renderSubItemContent(subItems[1])}
                        </div>
                    </div>
                );
                // Render third item (subItems[2])
                const item2Data = subItems[2];
                const item2IsTwoCol = item2Data?.itemName.includes("2 Col");
                const item2Class = determineSubItemWidthClass(item2Data);

                renderedBlocks.push(
                    <div className={item2Class} key={item2Data.id}>
                        {renderSubItemContent(item2Data, { isOuterTwoCol: item2IsTwoCol })}
                    </div>
                );
            } else if (item1IsOneCol && item2IsOneCol) {
                // Render first item (subItems[0])
                const item0Data = subItems[0];
                const item0IsTwoCol = item0Data?.itemName.includes("2 Col");
                const item0Class = determineSubItemWidthClass(item0Data);

                renderedBlocks.push(
                    <div className={`${item0Class} max-xl:mb-15`} key={item0Data.id}>
                        {renderSubItemContent(item0Data, { isOuterTwoCol: item0IsTwoCol })}
                    </div>
                );
                // Group and render second and third items (items 1 and 2)
                renderedBlocks.push(
                    <div className={oneColWidthClass} key="group-1-2">
                        <div className="max-xl:gap-15 flex flex-col gap-10">
                            {renderSubItemContent(subItems[1])}
                            {renderSubItemContent(subItems[2])}
                        </div>
                    </div>
                );
            } else {
                // No specific 3-item grouping, render all individually
                subItems.forEach((subItem, index) => {
                    const subItemIsTwoCol = subItem?.itemName.includes("2 Col");
                    const subItemClass = determineSubItemWidthClass(subItem);

                    renderedBlocks.push(
                        <div className={`${subItemClass} ${index === 0 ? "max-xl:mb-15" : ""}`} key={subItem.id}>
                            {renderSubItemContent(subItem, { isOuterTwoCol: subItemIsTwoCol })}
                        </div>
                    );
                });
            }
        } else {
            // Default rendering for other counts (1, 2, or 3+ items)
            subItems.forEach((subItem, index) => {
                const subItemIsTwoCol = subItem?.itemName.includes("2 Col");
                const subItemClass = determineSubItemWidthClass(subItem);

                renderedBlocks.push(
                    <div className={`${subItemClass} ${index === 0 ? "max-xl:mb-15" : ""}`} key={subItem.id}>
                        {renderSubItemContent(subItem, { isOuterTwoCol: subItemIsTwoCol })}
                    </div>
                );
            });
        }
    }

    return (
        <div
            className={`submenu-level-3 submenu--${layoutClass} xl:gap-8 xl:py-14.75 ${className}`}
            data-parent={`${layoutClass}-${layoutId}`}
            data-device={`${deviceType}`}
        >
            {renderedBlocks}
        </div>
    );
};

export default SubmenuLayoutFour;
